import {
	keysToSnakeCase,
	type SnakeCasedPropertiesDeep,
} from '@/core/utils/object';
import { ObservabilityStrategy } from '@/observability/observability';
import type { AnalyticsEvent } from './AnalyticsEvents';
import { analyticsConfig } from './analytics.config.client';
import type { AnalyticsDriver } from './driver.types';
import type { ANALYTICS_EVENTS } from './events.config';

const drivers: AnalyticsDriver[] = analyticsConfig.drivers.map((driver) =>
	driver.factory(),
);

export async function init(): Promise<void> {
	const responses = await Promise.allSettled(
		drivers.map(async (driver) => {
			if (driver.init) await driver.init();
		}),
	);

	for (const response of responses) {
		if (response.status === 'fulfilled') continue;

		ObservabilityStrategy.recordError(
			response.reason,
			`Could not initialize driver`,
		);
	}
}

async function pushToProviders(
	type: (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS],
	payload: SnakeCasedPropertiesDeep<Omit<AnalyticsEvent, 'type'>>,
) {
	const responses = await Promise.allSettled(
		drivers.map(async (driver) => {
			await driver.push(type, payload);
		}),
	);

	for (const response of responses) {
		if (response.status === 'fulfilled') continue;

		ObservabilityStrategy.recordError(
			response.reason,
			`Could not set analytics '${type}' event`,
		);
	}
}

export async function track(event: AnalyticsEvent): Promise<void> {
	const { type, ...attributes } = event;

	try {
		await pushToProviders(type, keysToSnakeCase(attributes));
	} catch (error) {
		ObservabilityStrategy.recordError(
			error,
			`Could not build or push analytics '${type}' event`,
		);
	}
}
